import { Button } from "@/components/ui/button";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { ListTodo, MoreVertical, Plus, List, LayoutGrid } from "lucide-react";
import CreateNoteModal from "@/components/create-note-modal";
import KanbanColumnDialog from "@/components/kanban-column-dialog";
import ShareDialog from "@/components/share-dialog";
import { ScheduleTaskDialog } from "@/components/tasks/subtask-components";
import { CreateTaskModal } from "@/components/tasks/create-task-modal";
import { EditTaskModal } from "@/components/tasks/edit-task-modal";
import { TaskViewDialog } from "@/components/tasks/task-view-dialog";
import { TaskBulkActionsDialog } from "@/components/tasks/task-bulk-actions-dialog";
import { TaskListTabs } from "@/components/tasks/task-list-tabs";
import { TaskQuickProgressDialog } from "@/components/tasks/task-quick-progress-dialog";
import { TaskProgressLogDialog } from "@/components/tasks/task-progress-log-dialog";
import { useTasksPage } from "@/hooks/useTasksPage";
import { useQuickCreateRoute } from "@/hooks/useQuickCreateRoute";

export default function TasksPage() {
  const {
    tasksLoading,
    tasks,
    tasksError,
    refetchTasks,
    goals,
    journalEntries,
    categories,
    columns,
    columnsLoading,
    assignedToMeLoading,
    assignedToMeTasks,
    assignedToMeError,
    refetchAssignedToMe,
    assignedToMeStats,
    assignedToOthersLoading,
    assignedToOthersTasks,
    sharedTasksLoading,
    sharedTasks,
    updateSharedTaskMutation,
    teamMembers,
    filters,
    updateFilters,
    isFiltersExpanded,
    setIsFiltersExpanded,
    activeMainTab,
    setActiveMainTab,
    filteredAndSortedTasks,
    groupedFilteredTasks,
    taskStats,
    subTasksByTaskId,
    displaySubTasksByTaskId,
    starredSubTaskItems,
    areAllTasksSelected,
    isProgressLogOpen,
    setIsProgressLogOpen,
    progressLogCategoryFilter,
    setProgressLogCategoryFilter,
    progressLogUserFilter,
    setProgressLogUserFilter,
    progressLogStartDate,
    setProgressLogStartDate,
    progressLogEndDate,
    setProgressLogEndDate,
    progressLogLoading,
    progressLogEntries,
    progressLogAssignees,
    isQuickProgressOpen,
    setIsQuickProgressOpen,
    quickProgressTaskId,
    setQuickProgressTaskId,
    quickProgressNote,
    setQuickProgressNote,
    quickProgressPopoverId,
    setQuickProgressPopoverId,
    quickProgressPopoverNote,
    setQuickProgressPopoverNote,
    selectedTasks,
    setSelectedTasks,
    isBulkActionsOpen,
    setIsBulkActionsOpen,
    pendingBulkUpdates,
    setPendingBulkUpdates,
    isCreateModalOpen,
    setIsCreateModalOpen,
    createTaskColumnId,
    setCreateTaskColumnId,
    editingTask,
    setEditingTask,
    viewingTask,
    setViewingTask,
    deletingTaskId,
    setDeletingTaskId,
    createNoteForTask,
    setCreateNoteForTask,
    isColumnDialogOpen,
    setIsColumnDialogOpen,
    editingColumn,
    setEditingColumn,
    deletingColumnId,
    setDeletingColumnId,
    shareDialogOpen,
    setShareDialogOpen,
    sharingTask,
    setSharingTask,
    schedulingTask,
    setSchedulingTask,
    sensors,
    collapsedGroups,
    toggleTaskMutation,
    toggleTopThreeMutation,
    quickProgressMutation,
    deleteColumnMutation,
    bulkUpdateTasksMutation,
    bulkDeleteTasksMutation,
    deleteSelectedTasks,
    toggleTaskSelection,
    toggleSelectAll,
    onShareTask,
    getLinkedItemName,
    isOverdue,
    getSubTaskProgress,
    getHideCompleted,
    onToggleHideCompleted,
    toggleGroupCollapsed,
    handleDragEnd,
    handleTaskMove,
    handleColumnDelete,
    handleColumnReorder,
    handleColumnSave,
    handleConfirmDelete,
    onScheduleTask,
    onSubtaskToggle,
    onSubtaskStar,
    onSubtaskDateChange,
    onSubtaskTextChange,
    onSubtaskReorder,
    onSubtaskDelete,
    setPerTaskCompletedOverrides,
    toast,
    user,
  } = useTasksPage();
  useQuickCreateRoute("task", () => setIsCreateModalOpen(true));

  if (tasksLoading) {
    return (
      <div className="flex items-center justify-center min-h-[50vh]">
        <div className="text-center">
          <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-2"></div>
          <p className="text-muted-foreground">Loading your tasks...</p>
        </div>
      </div>
    );
  }

  return (
    <>
      {/* Header */}
      <div className="bg-card border-b border-border px-4 sm:px-6 lg:px-8 py-4 sm:py-6">
        <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex-1 min-w-0">
            <h1 className="text-xl sm:text-2xl font-bold text-foreground flex items-center gap-2">
              <ListTodo className="h-5 w-5 sm:h-6 sm:w-6" />
              My Tasks
            </h1>
            <p className="text-sm sm:text-base text-muted-foreground mt-1">
              Organize and track your tasks across goals, milestones, and journal entries
            </p>
          </div>
          <div className="flex items-center gap-2 flex-wrap sm:flex-nowrap">
            <div className="flex items-center gap-1 border border-border rounded-lg p-1 flex-shrink-0">
              <Button
                variant={filters.viewMode === "list" ? "default" : "ghost"}
                size="icon"
                onClick={() => updateFilters({ viewMode: "list" })}
                data-testid="button-view-list"
                title="List View"
                className="h-9 w-9"
              >
                <List className="w-4 h-4" />
              </Button>
              <Button
                variant={filters.viewMode === "kanban" ? "default" : "ghost"}
                size="icon"
                onClick={() => updateFilters({ viewMode: "kanban" })}
                data-testid="button-view-kanban"
                title="Kanban View"
                className="h-9 w-9"
              >
                <LayoutGrid className="w-4 h-4" />
              </Button>
            </div>
            {filters.viewMode === "kanban" && (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="outline" size="sm" data-testid="button-manage-columns" className="hidden sm:inline-flex">
                    <MoreVertical className="w-4 h-4 sm:mr-2" />
                    <span className="hidden sm:inline">Manage Columns</span>
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  <DropdownMenuItem
                    onClick={() => { setEditingColumn(null); setIsColumnDialogOpen(true); }}
                    data-testid="button-create-column"
                  >
                    <Plus className="w-4 h-4 mr-2" />
                    Create Column
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            )}
            {selectedTasks.size >= 2 && (
              <Button
                onClick={() => setIsBulkActionsOpen(true)}
                size="sm"
                variant="secondary"
                data-testid="button-bulk-actions"
              >
                Bulk Actions ({selectedTasks.size})
              </Button>
            )}
            <Button
              onClick={() => setIsCreateModalOpen(true)}
              size="sm"
              className="bg-primary hover:bg-primary/90 text-primary-foreground"
              data-testid="button-create-task"
            >
              <Plus className="w-4 h-4 mr-2" />
              New Task
            </Button>
          </div>
        </div>
      </div>

      <TaskListTabs
        tasksError={tasksError}
        refetchTasks={refetchTasks}
        assignedToMeError={assignedToMeError}
        refetchAssignedToMe={refetchAssignedToMe}
        starredSubTaskItems={starredSubTaskItems}
        activeMainTab={activeMainTab}
        setActiveMainTab={setActiveMainTab}
        taskStats={taskStats}
        assignedToMeStats={assignedToMeStats}
        assignedToMeLoading={assignedToMeLoading}
        assignedToMeTasks={assignedToMeTasks}
        updateSharedTaskMutation={updateSharedTaskMutation}
        sharedTasksLoading={sharedTasksLoading}
        sharedTasks={sharedTasks}
        assignedToOthersLoading={assignedToOthersLoading}
        assignedToOthersTasks={assignedToOthersTasks}
        filters={filters}
        updateFilters={updateFilters}
        isFiltersExpanded={isFiltersExpanded}
        setIsFiltersExpanded={setIsFiltersExpanded}
        setIsQuickProgressOpen={setIsQuickProgressOpen}
        setIsProgressLogOpen={setIsProgressLogOpen}
        categories={categories}
        teamMembers={teamMembers}
        selectedTasks={selectedTasks}
        setSelectedTasks={setSelectedTasks}
        isBulkActionsOpen={isBulkActionsOpen}
        setIsBulkActionsOpen={setIsBulkActionsOpen}
        setPerTaskCompletedOverrides={setPerTaskCompletedOverrides}
        filteredAndSortedTasks={filteredAndSortedTasks}
        groupedFilteredTasks={groupedFilteredTasks}
        taskSubtasks={displaySubTasksByTaskId}
        areAllTasksSelected={areAllTasksSelected}
        toggleSelectAll={toggleSelectAll}
        setIsCreateModalOpen={setIsCreateModalOpen}
        isOverdue={isOverdue}
        getLinkedItemName={getLinkedItemName}
        getSubTaskProgress={getSubTaskProgress}
        toggleTaskMutation={toggleTaskMutation}
        toggleTopThreeMutation={toggleTopThreeMutation}
        setViewingTask={setViewingTask}
        setCreateNoteForTask={setCreateNoteForTask}
        setEditingTask={setEditingTask}
        setDeletingTaskId={setDeletingTaskId}
        toggleTaskSelection={toggleTaskSelection}
        onShareTask={onShareTask}
        quickProgressPopoverId={quickProgressPopoverId}
        setQuickProgressPopoverId={setQuickProgressPopoverId}
        quickProgressPopoverNote={quickProgressPopoverNote}
        setQuickProgressPopoverNote={setQuickProgressPopoverNote}
        quickProgressMutation={quickProgressMutation}
        onSubtaskToggle={onSubtaskToggle}
        onSubtaskStar={onSubtaskStar}
        onSubtaskDateChange={onSubtaskDateChange}
        onSubtaskTextChange={onSubtaskTextChange}
        onSubtaskReorder={onSubtaskReorder}
        onSubtaskDelete={onSubtaskDelete}
        getHideCompleted={getHideCompleted}
        onToggleHideCompleted={onToggleHideCompleted}
        onScheduleTask={onScheduleTask}
        collapsedGroups={collapsedGroups}
        toggleGroupCollapsed={toggleGroupCollapsed}
        handleDragEnd={handleDragEnd}
        sensors={sensors}
        columns={columns}
        columnsLoading={columnsLoading}
        handleTaskMove={handleTaskMove}
        handleColumnDelete={handleColumnDelete}
        handleColumnReorder={handleColumnReorder}
        setCreateTaskColumnId={setCreateTaskColumnId}
        setEditingColumn={setEditingColumn}
        setIsColumnDialogOpen={setIsColumnDialogOpen}
        toast={toast}
        tasks={tasks}
      />

      {/* Create Task Modal */}
      <CreateTaskModal
        open={isCreateModalOpen}
        onOpenChange={(open) => {
          setIsCreateModalOpen(open);
          if (!open) setCreateTaskColumnId(null);
        }}
        goals={goals}
        journalEntries={journalEntries}
        initialColumnId={createTaskColumnId}
      />

      {/* Edit Task Modal */}
      {editingTask && (
        <EditTaskModal
          open={!!editingTask}
          onOpenChange={(open) => !open && setEditingTask(null)}
          task={editingTask}
          goals={goals}
          journalEntries={journalEntries}
        />
      )}

      {/* View Task Dialog */}
      <TaskViewDialog
        viewingTask={viewingTask}
        setViewingTask={setViewingTask}
        setEditingTask={setEditingTask}
        isOverdue={isOverdue}
        subTasksByTaskId={subTasksByTaskId}
        getLinkedItemName={getLinkedItemName}
        onSubtaskStar={onSubtaskStar}
      />

      {/* Bulk Actions Dialog */}
      <TaskBulkActionsDialog
        isBulkActionsOpen={isBulkActionsOpen}
        setIsBulkActionsOpen={setIsBulkActionsOpen}
        selectedTasks={selectedTasks}
        tasks={tasks}
        currentUserId={user?.id}
        sharedTasks={sharedTasks}
        pendingBulkUpdates={pendingBulkUpdates}
        setPendingBulkUpdates={setPendingBulkUpdates}
        categories={categories}
        teamMembers={teamMembers}
        bulkUpdateTasksMutation={bulkUpdateTasksMutation}
        bulkDeleteTasksMutation={bulkDeleteTasksMutation}
        deleteSelectedTasks={deleteSelectedTasks}
        toast={toast}
      />

      {/* Delete Task Confirmation */}
      <AlertDialog open={!!deletingTaskId} onOpenChange={(open) => !open && setDeletingTaskId(null)}>
        <AlertDialogContent data-testid="dialog-delete-task">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Task</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete this task? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="button-cancel-delete">Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => deletingTaskId && handleConfirmDelete(deletingTaskId)}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              data-testid="button-confirm-delete"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Create Note Modal */}
      {createNoteForTask && (
        <CreateNoteModal
          isOpen={!!createNoteForTask}
          onClose={() => setCreateNoteForTask(null)}
          entityType="task"
          entityId={createNoteForTask.id}
          entityTitle={createNoteForTask.title}
        />
      )}

      {/* Column Dialog */}
      <KanbanColumnDialog
        isOpen={isColumnDialogOpen}
        onClose={() => { setIsColumnDialogOpen(false); setEditingColumn(null); }}
        column={editingColumn || undefined}
        onSave={handleColumnSave}
      />

      {/* Delete Column Confirmation */}
      <AlertDialog open={!!deletingColumnId} onOpenChange={(open) => !open && setDeletingColumnId(null)}>
        <AlertDialogContent data-testid="dialog-delete-column">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Column</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete this column? Tasks in this column will become uncategorized.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="button-cancel-delete-column">Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => deletingColumnId && deleteColumnMutation.mutate(deletingColumnId)}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              data-testid="button-confirm-delete-column"
            >
              {deleteColumnMutation.isPending ? "Deleting..." : "Delete"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Share Dialog */}
      {sharingTask && (
        <ShareDialog
          isOpen={shareDialogOpen}
          onClose={() => { setShareDialogOpen(false); setSharingTask(null); }}
          itemId={sharingTask.id}
          itemType="task"
          itemTitle={sharingTask.title}
        />
      )}

      {/* Add to Calendar Dialog */}
      <ScheduleTaskDialog
        open={!!schedulingTask}
        onOpenChange={(o) => { if (!o) setSchedulingTask(null); }}
        task={schedulingTask}
      />

      {/* Quick Progress Dialog */}
      <TaskQuickProgressDialog
        isQuickProgressOpen={isQuickProgressOpen}
        setIsQuickProgressOpen={setIsQuickProgressOpen}
        quickProgressTaskId={quickProgressTaskId}
        setQuickProgressTaskId={setQuickProgressTaskId}
        quickProgressNote={quickProgressNote}
        setQuickProgressNote={setQuickProgressNote}
        quickProgressMutation={quickProgressMutation}
        tasks={tasks}
      />

      {/* Progress Activity Log Dialog */}
      <TaskProgressLogDialog
        isProgressLogOpen={isProgressLogOpen}
        setIsProgressLogOpen={setIsProgressLogOpen}
        progressLogCategoryFilter={progressLogCategoryFilter}
        setProgressLogCategoryFilter={setProgressLogCategoryFilter}
        progressLogUserFilter={progressLogUserFilter}
        setProgressLogUserFilter={setProgressLogUserFilter}
        progressLogStartDate={progressLogStartDate}
        setProgressLogStartDate={setProgressLogStartDate}
        progressLogEndDate={progressLogEndDate}
        setProgressLogEndDate={setProgressLogEndDate}
        progressLogLoading={progressLogLoading}
        progressLogEntries={progressLogEntries}
        progressLogAssignees={progressLogAssignees}
        categories={categories}
      />
    </>
  );
}
